import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Workspace } from "../lib/api";
import { LoadingButton } from "./Spinner";
import { useConfirm } from "./ConfirmDialog";

interface Props {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}

// Modal version of the Agents page. Same affordances — register a new
// agent, see connected agents, revoke/delete — but as an overlay so
// users can manage agents without leaving their current request.
export default function AgentsModal({ workspace, open, onClose }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [name, setName] = useState("My machine");
  const [created, setCreated] = useState<{ id: string; name: string; token: string; wsUrl: string } | null>(null);

  const list = useQuery({
    queryKey: ["agents", workspace.id],
    queryFn: () => api.agents.list(workspace.id),
    refetchInterval: open ? 5_000 : false,
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => api.agents.create(workspace.id, name.trim() || "Agent"),
    onSuccess: (a) => {
      setCreated(a);
      setName("My machine");
      qc.invalidateQueries({ queryKey: ["agents", workspace.id] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.agents.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents", workspace.id] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.agents.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents", workspace.id] }),
  });

  // Escape to close, like other modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-3xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Agents</h2>
            <span className="text-xs muted-strong">— {workspace.name}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-5 bg-bg">
          <p className="text-sm muted">
            An agent runs on your machine (or a server) and executes outbound API requests for you — Bruno-style local execution.
            Anything the agent can reach (private networks, VPN, custom certs), the request can hit.
            Without an agent, requests still run from the Cloudflare edge.
          </p>

          <form
            className="card p-5 space-y-3"
            onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          >
            <h3 className="font-semibold">Register a new agent</h3>
            <p className="text-sm muted">Give it a name so you can tell agents apart in the list below.</p>
            <div className="flex gap-2">
              <input className="input flex-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Agent name" />
              <LoadingButton type="submit" className="btn-primary" pending={create.isPending} pendingLabel="Creating…">
                Create
              </LoadingButton>
            </div>
          </form>

          {created && <NewlyCreatedAgent agent={created} onDismiss={() => setCreated(null)} />}

          <div className="card p-5 space-y-3">
            <h3 className="font-semibold">All agents</h3>
            {(list.data?.agents ?? []).length === 0 && (
              <div className="empty-card">No agents yet.</div>
            )}
            <ul className="space-y-2">
              {(list.data?.agents ?? []).map((a) => (
                <li key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-surface-2 p-3">
                  <span className={"h-2.5 w-2.5 rounded-full " + (a.online ? "bg-good" : "bg-text-faint")} aria-label={a.online ? "online" : "offline"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.name}</div>
                    <div className="text-xs muted-strong truncate">
                      {a.online ? "online" : a.last_seen_at ? `last seen ${new Date(a.last_seen_at).toLocaleString()}` : "never connected"}
                      {a.revoked_at && " · revoked"}
                    </div>
                  </div>
                  {!a.revoked_at && (
                    <button className="btn" onClick={async () => {
                      if (await confirm({ title: `Revoke "${a.name}"?`, description: "The agent's token stops working immediately.", danger: true })) revoke.mutate(a.id);
                    }}>Revoke</button>
                  )}
                  <button className="btn-danger" onClick={async () => {
                    if (await confirm({ title: `Delete "${a.name}"?`, danger: true })) remove.mutate(a.id);
                  }}>Delete</button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-5 space-y-3">
            <h3 className="font-semibold">Install the agent</h3>
            <p className="text-sm muted">From a checkout of this repo:</p>
            <pre className="code-block">
{`# install once
cd apps/agent && npm install

# login with the URL shown when you create an agent (above) ⤴
node bin/rest-agent.js login 'ws://…/api/agents/ws?token=…'

# then keep it running
node bin/rest-agent.js run`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewlyCreatedAgent({ agent, onDismiss }: { agent: { id: string; name: string; token: string; wsUrl: string }; onDismiss: () => void }) {
  return (
    <div className="card p-5 space-y-3 border-accent">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Agent "{agent.name}" created</h3>
        <button className="btn" onClick={onDismiss}>Dismiss</button>
      </div>
      <p className="text-sm muted">
        Copy the connection URL below — it's the <b className="text-text">only time</b> you'll see this token in plaintext.
        If you lose it, revoke this agent and create a new one.
      </p>
      <div>
        <label className="label">Token</label>
        <pre className="code-block break-all">{agent.token}</pre>
      </div>
      <div>
        <label className="label">Connection URL</label>
        <pre className="code-block break-all">{agent.wsUrl}</pre>
      </div>
      <div>
        <label className="label">Login command</label>
        <pre className="code-block break-all">{`node bin/rest-agent.js login '${agent.wsUrl}'`}</pre>
      </div>
    </div>
  );
}
